import * as React from 'react';
import { Nav, INavLink, INavLinkGroup } from '@fluentui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HomeView } from '../data-structures/enums';
import { navPanelStyle, navIconStyle } from "../styles/NavMenuStyle"
import { RootState } from '../redux';
import { connect, ConnectedProps } from 'react-redux';
import { LanguageContext } from "../utilities/LocalizationModule"

const mapStateToProps = (state: RootState) => {
    return {
        isGlobalAdmin: state.user.isGlobalAdmin
    }
}

const connector = connect(mapStateToProps, null)
type PropsFromRedux = ConnectedProps<typeof connector>;
type NavMenuProps = PropsFromRedux;

// Maps a Fluent Nav link key (a HomeView value) to its route path.
const VIEW_PATHS: Partial<Record<HomeView, string>> = {
    [HomeView.Dashboard]: "/dashboard",
    [HomeView.Users]: "/users",
    [HomeView.Subscriptions]: "/subscriptions",
    [HomeView.PartnersOverview]: "/partners/overview",
    [HomeView.PartnersRequests]: "/partners/requests",
};

// Derives the selected Nav key from the current path. Prefix matching so nested paths
// (/subscriptions/5, /partners/overview) still highlight the correct item.
const selectedKeyForPath = (pathname: string): string => {
    if (pathname.startsWith("/partners/overview")) return HomeView.PartnersOverview;
    if (pathname.startsWith("/partners/requests")) return HomeView.PartnersRequests;
    if (pathname.startsWith("/users")) return HomeView.Users;
    if (pathname.startsWith("/subscriptions")) return HomeView.Subscriptions;
    return HomeView.Dashboard;
};

const NavMenu: React.FC<NavMenuProps> = ({
    isGlobalAdmin
}: NavMenuProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const navigate = useNavigate();
    const location = useLocation();

    // Seed the Partners expander from the path so a refresh on /partners/* shows it open;
    // it stays a local toggle so the user can still collapse/expand manually.
    const [isPartnersExpanded, setPartnersExpanded] = React.useState<boolean>(
        () => location.pathname.startsWith("/partners")
    );

    const selectedKey = selectedKeyForPath(location.pathname);

    const navLinkGroups: INavLinkGroup[] = React.useMemo(() => [
        {
            links: [
                {
                    name: "Dashboard",
                    url: "#",
                    key: HomeView.Dashboard,
                    iconProps: {
                        iconName: "LineChart",
                        styles: navIconStyle,
                    }
                },
                {
                    name: languageStrings.Users,
                    url: "#",
                    key: HomeView.Users,
                    iconProps: {
                        iconName: "People",
                        styles: navIconStyle
                    }
                },
            ],
        },
    ], [isPartnersExpanded]);

    navLinkGroups[0].links = !navLinkGroups[0].links.some(l => l.key == HomeView.Subscriptions) ? navLinkGroups[0].links.concat([
        {
            name: languageStrings.Subscriptions,
            url: "#",
            key: HomeView.Subscriptions,
            iconProps: {
                iconName: "PaymentCard",
                styles: navIconStyle,
            }
        },
    ]) : navLinkGroups[0].links;

    //The check needs to be done to see if the nav link has been added to the list yet, or otherwise dubplicates are created on each rerender.

    navLinkGroups[0].links = isGlobalAdmin && !navLinkGroups[0].links.some(l => l.key == HomeView.Partners) ? navLinkGroups[0].links.concat([{
        name: languageStrings.Partners,
        url: "#",
        key: HomeView.Partners,
        iconProps: {
            iconName: "Work",
            styles: navIconStyle
        },
        isExpanded: isPartnersExpanded,
        links: [
            {
                name: languageStrings.Overview,
                url: '#',
                key: HomeView.PartnersOverview,
                iconProps: {
                    iconName: "ViewListGroup",
                    styles: navIconStyle
                }
            },
            {
                name: languageStrings.Requests,
                url: "#",
                key: HomeView.PartnersRequests,
                iconProps: {
                    iconName: "PeopleAlert",
                    styles: navIconStyle
                }
            },
        ]
    }]) : navLinkGroups[0].links;

    function _onLinkClick(ev?: React.MouseEvent<HTMLElement>, item?: INavLink) {
        ev?.preventDefault();
        if (!item) return;
        if (item.key === HomeView.Partners) {
            // Parent group node — toggle expand/collapse only, no navigation.
            setPartnersExpanded(prev => !prev);
            return;
        }
        const path = VIEW_PATHS[item.key as HomeView];
        if (path) navigate(path);
    }

    return (
        <Nav
            onLinkClick={_onLinkClick}
            selectedKey={selectedKey}
            ariaLabel="Navigation Menu"
            styles={navPanelStyle}
            groups={navLinkGroups}
        />
    );
};

export default connector(NavMenu)
